import React, { forwardRef } from "react";
import { fmtCurrency, fmtNumber } from "@/lib/api";
import { CRIMSON } from "@/lib/chartTheme";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  Cell,
} from "recharts";

// Fixed-width, light-themed print layout captured to PDF by html2pdf.
// Rendered off-screen (0x0 overflow:hidden wrapper) with real dimensions so
// recharts renders synchronously and html2canvas can capture the full node.
const NAVY = "#0f172a";
const SLATE = "#475569";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const W = 720;

const AGING = { "0-30": "#059669", "31-60": "#d97706", "61-90": "#ea580c", "90+": CRIMSON };
const REMARK_PLUGINS = [remarkGfm];

const ACTIVITY_TYPES = [
  { key: "calls", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "tasks", label: "Tasks" },
  { key: "meetings", label: "Meetings" },
  { key: "notes", label: "Notes" },
];

// Print-friendly markdown renderers so the AI report translates cleanly to PDF.
// `breakInside: avoid` keeps each block atomic so html2pdf never splits a line
// across a page boundary.
const NO_SPLIT = { breakInside: "avoid", pageBreakInside: "avoid" };

const mdComponents = {
  h1: ({ node, ...p }) => (
    <h1
      style={{
        fontSize: 20,
        color: NAVY,
        margin: "18px 0 10px",
        lineHeight: 1.25,
        pageBreakAfter: "avoid",
        breakAfter: "avoid",
        ...NO_SPLIT,
      }}
      {...p}
    />
  ),
  h2: ({ node, ...p }) => (
    <h2
      style={{
        fontSize: 15,
        color: NAVY,
        margin: "16px 0 8px",
        paddingBottom: 4,
        borderBottom: `1px solid ${BORDER}`,
        lineHeight: 1.3,
        pageBreakAfter: "avoid",
        breakAfter: "avoid",
        ...NO_SPLIT,
      }}
      {...p}
    />
  ),
  h3: ({ node, ...p }) => (
    <h3
      style={{ fontSize: 13, color: CRIMSON, margin: "12px 0 6px", pageBreakAfter: "avoid", breakAfter: "avoid", ...NO_SPLIT }}
      {...p}
    />
  ),
  h4: ({ node, ...p }) => (
    <h4 style={{ fontSize: 12, color: NAVY, margin: "10px 0 4px", fontWeight: 700, ...NO_SPLIT }} {...p} />
  ),
  p: ({ node, ...p }) => (
    <p style={{ fontSize: 11.5, lineHeight: 1.6, color: SLATE, margin: "0 0 9px", ...NO_SPLIT }} {...p} />
  ),
  ul: ({ node, ordered, ...p }) => (
    <ul style={{ margin: "0 0 10px", paddingLeft: 20, fontSize: 11.5, lineHeight: 1.6, color: SLATE }} {...p} />
  ),
  ol: ({ node, ordered, ...p }) => (
    <ol style={{ margin: "0 0 10px", paddingLeft: 20, fontSize: 11.5, lineHeight: 1.6, color: SLATE }} {...p} />
  ),
  li: ({ node, ...p }) => <li style={{ margin: "0 0 4px", ...NO_SPLIT }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: NAVY, fontWeight: 700 }} {...p} />,
  em: ({ node, ...p }) => <em style={{ color: SLATE }} {...p} />,
  a: ({ node, ...p }) => <span style={{ color: CRIMSON }} {...p} />,
  hr: () => <hr style={{ border: 0, borderTop: `1px solid ${BORDER}`, margin: "14px 0" }} />,
  blockquote: ({ node, ...p }) => (
    <blockquote
      style={{
        margin: "0 0 10px",
        padding: "6px 12px",
        borderLeft: `3px solid ${CRIMSON}`,
        background: "#faf5f6",
        color: SLATE,
        fontSize: 11.5,
        ...NO_SPLIT,
      }}
      {...p}
    />
  ),
  code: ({ node, inline, ...p }) => (
    <code
      style={{
        background: "#f1f5f9",
        borderRadius: 3,
        padding: inline ? "1px 4px" : "8px 10px",
        fontFamily: "Consolas, Menlo, monospace",
        fontSize: 10.5,
        color: NAVY,
        display: inline ? "inline" : "block",
      }}
      {...p}
    />
  ),
  table: ({ node, ...p }) => (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 10.5,
        margin: "0 0 12px",
        ...NO_SPLIT,
      }}
      {...p}
    />
  ),
  thead: ({ node, ...p }) => <thead {...p} />,
  tr: ({ node, ...p }) => <tr style={NO_SPLIT} {...p} />,
  th: ({ node, ...p }) => (
    <th
      style={{
        textAlign: "left",
        padding: "6px 8px",
        background: "#f8fafc",
        borderBottom: `2px solid ${NAVY}`,
        color: NAVY,
        fontWeight: 700,
      }}
      {...p}
    />
  ),
  td: ({ node, ...p }) => (
    <td style={{ padding: "5px 8px", borderBottom: `1px solid ${BORDER}`, color: SLATE }} {...p} />
  ),
};

function SectionTitle({ children }) {
  return (
    <h2 style={{ fontSize: 14, color: NAVY, margin: "0 0 8px", pageBreakAfter: "avoid" }}>{children}</h2>
  );
}

const PrintReport = forwardRef(function PrintReport(
  { analytics, reportMd, generatedAt, rangeLabel },
  ref
) {
  const a = analytics || {};
  const k = a.kpis || {};
  const stages = a.pipeline_by_stage || [];
  const aging = (a.deal_aging && a.deal_aging.buckets) || [];
  const trends = a.trends || [];
  const leaderboard = a.leaderboard || [];
  const activity = a.activity_analysis || {};
  const activityByRep = activity.by_rep || [];
  const activityByStatus = activity.by_status || [];
  const activityTotal = activity.total || 0;
  const activityCount = (key) => (activity.by_type || []).find((t) => t.type === key)?.count ?? 0;
  const activityLabel = (key) => (ACTIVITY_TYPES.find((t) => t.key === key) || {}).label || key;

  return (
    <div
      aria-hidden="true"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", left: 0, top: 0 }}
    >
      <div
        ref={ref}
        style={{
          width: W,
          background: "#ffffff",
          color: NAVY,
          fontFamily: "Arial, Helvetica, sans-serif",
          padding: "0 32px 32px",
        }}
      >
        {/* Header */}
        <div style={{ borderBottom: `3px solid ${CRIMSON}`, paddingBottom: 12, marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: 24, color: NAVY }}>Senior Sales Analyst Report</h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: MUTED }}>
            {generatedAt ? `Generated ${new Date(generatedAt).toLocaleString()}` : ""}
            {rangeLabel ? `  ·  Period: ${rangeLabel}` : ""}
          </p>
        </div>

        {/* KPIs */}
        <SectionTitle>Key Metrics</SectionTitle>
        <div style={{ display: "flex", flexWrap: "nowrap", gap: 10, marginBottom: 20 }}>
          {[
            ["Open Pipeline", fmtCurrency(k.open_pipeline_value), `${fmtNumber(k.open_deals)} open`],
            ["Weighted Forecast", fmtCurrency(k.weighted_forecast), "prob-adjusted"],
            ["Won Revenue", fmtCurrency(k.won_value), `${fmtNumber(k.won_deals)} won`],
            [
              "Win Rate",
              `${k.win_rate ?? 0}%`,
              k.avg_sales_cycle_days != null ? `${k.avg_sales_cycle_days}d cycle` : `${fmtNumber(k.lost_deals)} lost`,
            ],
          ].map(([label, val, sub]) => (
            <div
              key={label}
              style={{
                flex: "1 1 0",
                minWidth: 0,
                boxSizing: "border-box",
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              <div style={{ fontSize: 10, color: MUTED }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: NAVY }}>{val}</div>
              <div style={{ fontSize: 9, color: "#94a3b8" }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Sales Rep Activity Snapshot */}
        <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
          <SectionTitle>Sales Rep Activity Snapshot{rangeLabel ? ` — ${rangeLabel}` : ""}</SectionTitle>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {ACTIVITY_TYPES.map((t) => (
              <div
                key={t.key}
                style={{
                  flex: 1,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                  padding: "8px 6px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{fmtNumber(activityCount(t.key))}</div>
                <div style={{ fontSize: 9, color: MUTED }}>{t.label}</div>
              </div>
            ))}
          </div>
          {activityTotal > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                  <th style={{ textAlign: "left", padding: 4 }}>Rep</th>
                  {ACTIVITY_TYPES.map((t) => (
                    <th key={t.key} style={{ textAlign: "right", padding: 4 }}>
                      {t.label}
                    </th>
                  ))}
                  <th style={{ textAlign: "right", padding: 4, fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {activityByRep.map((r) => (
                  <tr key={r.owner_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: 4 }}>{r.name}</td>
                    {ACTIVITY_TYPES.map((t) => (
                      <td key={t.key} style={{ textAlign: "right", padding: 4, color: SLATE }}>
                        {fmtNumber(r[t.key] ?? 0)}
                      </td>
                    ))}
                    <td style={{ textAlign: "right", padding: 4, fontWeight: 700 }}>{fmtNumber(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: 10.5, color: MUTED, margin: 0 }}>
              No activities in this period.
            </p>
          )}
          {activityByStatus.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
                Status breakdown
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {activityByStatus.map((grp) => (
                  <div
                    key={grp.type}
                    style={{
                      border: `1px solid ${BORDER}`,
                      borderRadius: 6,
                      padding: "6px 10px",
                      minWidth: 150,
                    }}
                  >
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, marginBottom: 3 }}>
                      {activityLabel(grp.type)} · {fmtNumber(grp.total)}
                    </div>
                    {grp.statuses.map((s) => (
                      <div
                        key={s.status}
                        style={{ display: "flex", justifyContent: "space-between", fontSize: 9.5, color: SLATE }}
                      >
                        <span>{s.status}</span>
                        <span style={{ fontWeight: 600 }}>{fmtNumber(s.count)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pipeline by stage */}
        {stages.length > 0 && (
          <div style={{ marginBottom: 16, pageBreakInside: "avoid" }}>
            <SectionTitle>Pipeline by Stage</SectionTitle>
            <BarChart width={W - 32} height={200} data={stages} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={BORDER} />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} fontSize={10} stroke={MUTED} />
              <YAxis type="category" dataKey="label" width={110} fontSize={10} stroke={MUTED} />
              <Bar dataKey="value" fill={CRIMSON} radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </div>
        )}

        {/* Deal aging */}
        {aging.length > 0 && (
          <div style={{ marginBottom: 16, pageBreakInside: "avoid" }}>
            <SectionTitle>Deal Aging (open deals)</SectionTitle>
            <BarChart width={W - 32} height={180} data={aging}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={BORDER} />
              <XAxis dataKey="range" fontSize={10} stroke={MUTED} />
              <YAxis fontSize={10} stroke={MUTED} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {aging.map((b) => (
                  <Cell key={b.range} fill={AGING[b.range]} />
                ))}
              </Bar>
            </BarChart>
          </div>
        )}

        {/* Trends */}
        {trends.length > 0 && (
          <div style={{ marginBottom: 16, pageBreakInside: "avoid" }}>
            <SectionTitle>Revenue Trends (12 months)</SectionTitle>
            <LineChart width={W - 32} height={190} data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="month" fontSize={10} stroke={MUTED} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} fontSize={10} stroke={MUTED} />
              <Legend />
              <Line type="monotone" dataKey="created_value" name="Created" stroke={MUTED} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="won_value" name="Won" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
            <SectionTitle>Rep Leaderboard</SectionTitle>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                  <th style={{ textAlign: "left", padding: 4 }}>Rep</th>
                  <th style={{ textAlign: "right", padding: 4 }}>Won</th>
                  <th style={{ textAlign: "right", padding: 4 }}>Open</th>
                  <th style={{ textAlign: "right", padding: 4 }}>Win%</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.slice(0, 10).map((r) => (
                  <tr key={r.owner_id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <td style={{ padding: 4 }}>{r.name}</td>
                    <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(r.won_value)}</td>
                    <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(r.open_value)}</td>
                    <td style={{ textAlign: "right", padding: 4 }}>{r.win_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI narrative report */}
        <div style={{ borderTop: `2px solid ${CRIMSON}`, paddingTop: 16, marginTop: 4 }}>
          {reportMd ? (
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={mdComponents}>
              {reportMd}
            </ReactMarkdown>
          ) : (
            <p style={{ color: MUTED, fontSize: 11.5 }}>
              No AI narrative report generated yet. Generate one in the Sales Analysis tab to include it here.
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

export default PrintReport;
